// --- Blindaje contra el autocompletado del navegador -------------------------
// Chrome/Safari autocompletan campos que NO son de login usando el perfil guardado
// (mail, nombre, dirección). En Ágora eso rompe datos reales: en el onboarding el
// navegador metía el MAIL del vendor dentro del input de usuario y, como el campo
// corta en 20 caracteres, quedaba guardado el mail mutilado como dirección pública
// —`canceloreina@gmail.com` terminaba en `agora.red/canceloreinagmail.c`—. Pasó
// ~22 veces en 180 días. El mismo riesgo existe en cada form donde el vendor carga
// datos de OTRA persona (alta de cliente, invitación de staff): ahí el autofill le
// escribe los datos propios encima.
//
// La regla es: el autocompletado es OPT-IN, no opt-out. Un input solo se
// autocompleta si declara explícitamente para qué sirve (`autocomplete="email"`,
// `"tel-national"`, `"one-time-code"`, …). Todo lo demás queda apagado.
//
// Por qué un guard global y no editar los ~160 `<input>` sueltos: es una sola
// regla, no 160 decisiones, y así también cubre los inputs que se agreguen mañana.
// Los componentes compartidos (InputField/InputUnit) además lo aplican en el
// render, así el atributo ya está en el primer paint y no depende del observer.
//
// OJO — esto NO alcanza para campos que el navegador clasifica como credenciales:
// Chrome ignora `autocomplete="off"` a propósito cuando cree que el input es de
// usuario/contraseña (para no romper los gestores de claves). Ahí hay que evitar
// que lo clasifique así, no pedirle que se calle: ver el input de usuario en
// SelfOnboarding/steps/SiteData.tsx.

/** Marca de "ya procesado" — evita re-pisar el mismo nodo en cada commit de React. */
const PROCESSED_ATTR = 'data-agora-autofill-guarded';

/**
 * Solo los inputs que escriben texto. Los checkbox/radio/file/hidden y los botones
 * no reciben autofill, así que tocarlos sería ensuciar el DOM al pedo.
 */
const AUTOFILLABLE_TYPES = new Set([
  'text',
  'search',
  'url',
  'tel',
  'email',
  'number',
  'password',
  'date',
  'month',
  'week',
  'time',
  'datetime-local'
]);

function shouldGuard(el: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (el.hasAttribute(PROCESSED_ATTR)) return false;
  // Opt-in explícito: el campo ya declaró qué autocompletar. No lo tocamos —
  // incluye los `autocomplete="off"` puestos a mano, que quedan igual.
  if (el.hasAttribute('autocomplete')) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  // `el.type` normaliza: un type inválido o ausente cae en 'text'.
  return AUTOFILLABLE_TYPES.has(el.type);
}

function guard(el: HTMLInputElement | HTMLTextAreaElement): void {
  if (!shouldGuard(el)) return;
  el.setAttribute(PROCESSED_ATTR, '');
  el.setAttribute('autocomplete', 'off');
  // Los gestores de contraseñas ignoran `autocomplete` y usan sus propios opt-out.
  // Sin esto, 1Password/LastPass/Bitwarden/Dashlane siguen ofreciendo autocompletar.
  el.setAttribute('data-1p-ignore', '');
  el.setAttribute('data-lpignore', 'true');
  el.setAttribute('data-bwignore', '');
  el.setAttribute('data-form-type', 'other');
}

function guardTree(root: ParentNode): void {
  root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach(guard);
}

export function installDomAutofillGuard(): void {
  if (typeof document === 'undefined' || typeof MutationObserver !== 'function') return;
  const doc = document as Document & { __agoraAutofillGuard?: boolean };
  if (doc.__agoraAutofillGuard) return; // idempotente
  doc.__agoraAutofillGuard = true;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Solo miramos lo que se AGREGÓ, nunca el árbol entero: el callback corre en
      // cada commit de React y barrer todo el documento se siente en gama baja.
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
          guard(node);
          continue;
        }
        guardTree(node);
      }
    }
  });

  const start = () => {
    guardTree(document);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  // El guard se instala antes del primer render, cuando `document.body` puede no
  // existir todavía si el script se evalúa en el <head>.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
